import { motion, AnimatePresence } from 'motion/react';
import { X, MapPin, Heart, MessageCircle, Lock, MoreHorizontal, AlertTriangle, Ban, Shield, Play, UserPlus, UserCheck, Loader2 } from 'lucide-react';
import goldIcon from '../assets/gold icon.png';
import { EconomyService, UserService } from '../services/api';
import { FRIEND_REQUEST_COST, SWIPE_COSTS } from '../constants/economy';
import { isSimilarToMe } from './utils/similarity';
import { FlagImage, countryToFlagCode } from './utils/countryFlags';
import { getFlagImage } from '../assets/flags';
import { useTheme } from './ThemeProvider';
import { Yap, User, UserRank } from '../types';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useSettings } from './SettingsContext';
import { getRankRingParams, sanitizeLocation } from './utils/rankUtils';
import RankBadge from './RankBadge';
import { DEFAULT_BANNER_URL } from '../constants/assets';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { LegionAvatarWrapper } from './LegionAvatarWrapper';
import { formatTimeAgo, calculateAge } from './utils/timeFormat';
import { toast } from 'sonner';
import { ProfilePictureModal } from './ProfilePictureModal';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getDbSafe } from '../services/firebaseConfig';
import { doc, updateDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { FormattedText } from './FormattedText';
import { isMaybeVideo } from './utils/yapUtils';
import { IdentityTag } from './IdentityTag';
import { categoryStyles, getCategoryByTag } from '../constants/identities';
import { SocialLinks } from './ui/SocialLinks';
import { FriendBadge } from './ui/FriendBadge';
import { SimilarBadge } from './ui/SimilarBadge';
import { YapImageGrid } from './ui/YapImageGrid';
import { useActivityStatus } from '../hooks/useActivityStatus';
import { GlowDot } from './ui/RecentlyActiveBadge';


interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

export function UserDetailModal({ isOpen, onClose, user }: UserDetailModalProps) {
  const { theme } = useTheme();
  const activity = useActivityStatus(isOpen ? user?.id : null, { live: true });
  const {
    userProfile: currentUserProfile,
    blockUser,
    isFriend,
    sendFriendRequest,
    cancelFriendRequest,
    hasPendingRequestTo,
    hasPendingRequestFrom,
    updateProfile,
  } = useUserData();
  const { isBlockedId } = useContentFilter();
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [viewingPortfolioIndex, setViewingPortfolioIndex] = useState<number | null>(null);
  const [viewingYapImageIndex, setViewingYapImageIndex] = useState<number | null>(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectSentLocal, setConnectSentLocal] = useState(false);

  const targetUid = String(user?.id || '');
  const isAlreadyFriend = isFriend(targetUid);
  const isPendingOutgoing = connectSentLocal || hasPendingRequestTo(targetUid);
  const isPendingIncoming = hasPendingRequestFrom(targetUid);
  const balance = currentUserProfile?.goldBalance ?? currentUserProfile?.gold ?? 0;
  // Similar users cost the matching price (30); everyone else the base price
  // (10) — same rule as the discover page.
  const isSimilar = isSimilarToMe(currentUserProfile, user);
  const connectCost = isSimilar ? SWIPE_COSTS.matching : FRIEND_REQUEST_COST;

  const connectState: 'friend' | 'sent' | 'accept' | 'idle' | 'loading' =
    connectLoading ? 'loading' :
    isAlreadyFriend ? 'friend' :
    isPendingOutgoing ? 'sent' :
    isPendingIncoming ? 'accept' :
    'idle';

  const handleConnect = async () => {
    if (!user || !currentUserProfile || connectLoading) return;
    if (connectState === 'friend') return;

    // Take back a request we already sent (e.g. tapped Connect by accident).
    if (connectState === 'sent') {
      setConnectLoading(true);
      try {
        // Same refund as the preview sheet and the full profile.
        await cancelFriendRequest(targetUid, connectCost);
        setConnectSentLocal(false);
      } catch {
        toast.error('Failed to cancel request');
      } finally {
        setConnectLoading(false);
      }
      return;
    }

    // Accepting an incoming request is free — no gold deduction.
    if (connectState === 'accept') {
      setConnectLoading(true);
      try {
        await sendFriendRequest(
          targetUid,
          user.name,
          user.username || '@user',
          (user as any).portfolioPreviews?.[0] ?? user.image ?? user.avatar ?? '',
        );
        setConnectSentLocal(true);
      } catch {
        toast.error('Failed to accept request');
      } finally {
        setConnectLoading(false);
      }
      return;
    }

    // Idle → costs 30 gold for similar users, else 10. Matches the discover page.
    if (balance < connectCost) {
      toast.error('Not enough gold', { description: `You need ${connectCost} gold to connect.` });
      return;
    }
    setConnectLoading(true);
    try {
      const econ = await EconomyService.deductGold(
        String(currentUserProfile.id), connectCost, 'user_detail_connect',
      );
      if (econ?.success && econ.newBalance !== undefined) {
        updateProfile({ gold: econ.newBalance, goldBalance: econ.newBalance } as any);
      }
      await sendFriendRequest(
        targetUid,
        user.name,
        user.username || '@user',
        (user as any).portfolioPreviews?.[0] ?? user.image ?? user.avatar ?? '',
      );
      setConnectSentLocal(true);
    } catch {
      toast.error('Failed to send request');
    } finally {
      setConnectLoading(false);
    }
  };


  const isOwner = currentUserProfile?.id === user.id;
  const isPrivate = !isOwner && user.profilePrivacy === 'private';
  // Prioritize portfolioPreviews for the grid display (agreed-upon square crops)
  // 🔒 ENSURE 4 SLOTS: We pad to 4 elements to ensure the grid always matches the layout
  // and indices align correctly for the video viewer.
  const portfolioPreviews = (() => {
    const base = [...(user.portfolioPreviews || [])];
    while (base.length < 4) base.push("");
    return base.slice(0, 4);
  })();

  const portfolioImages = (() => {
    const base = [...(user.portfolio || [])];
    while (base.length < 4) base.push("");
    return base.slice(0, 4);
  })();

  // Use the banner image if available, otherwise the default placeholder
  const coverImage = user.bannerImage || DEFAULT_BANNER_URL;

  // The user document carries a denormalized `recentYap` snapshot that's only
  // updated when a yap is CREATED — it doesn't get rewritten when the user
  // posts a newer yap to a tribe/forum, and the deleteYap path only clears it
  // when the deleted yap matches the cached one. End result: the snapshot
  // shows deleted yaps or stale content. Source-of-truth fetch from the yaps
  // collection (newest non-deleted) and prefer that over the denormalized field.
  const [fetchedRecentYap, setFetchedRecentYap] = useState<{
    id: string;
    content: string;
    timestamp: string;
    likes: number;
    comments: number;
    images: string[];
  } | null>(null);
  const [recentYapLoaded, setRecentYapLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let active = true;
    setRecentYapLoaded(false);
    (async () => {
      try {
        const db = await getDbSafe();
        const q = query(
          collection(db, 'yaps'),
          where('userId', '==', String(user.id)),
          orderBy('timestamp', 'desc'),
          limit(5),
        );
        const snap = await getDocs(q);
        const found = snap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) }))
          .find((d: any) => !d.deleted && !d.is_hidden);
        if (!active) return;
        if (found) {
          const ts = found.timestamp;
          const iso = ts?.toDate ? ts.toDate().toISOString()
            : typeof ts === 'string' ? ts
            : new Date().toISOString();
          const imgs: string[] = Array.isArray(found.images)
            ? found.images.filter(Boolean)
            : (found.image ? [found.image] : []);
          setFetchedRecentYap({
            id: found.id,
            content: found.content || '',
            timestamp: iso,
            likes: found.metrics?.likes ?? found.likes ?? 0,
            comments: found.metrics?.comments ?? found.comments ?? 0,
            images: imgs,
          });
        } else {
          setFetchedRecentYap(null);
        }
      } catch (err) {
        console.warn('[UserDetailModal] Failed to fetch recent yap:', err);
        if (active) setFetchedRecentYap(null);
      } finally {
        if (active) setRecentYapLoaded(true);
      }
    })();
    return () => { active = false; };
  }, [isOpen, user?.id]);

  // Prefer the freshly-fetched yap. Until the fetch resolves, fall back to the
  // denormalized snapshot so the section isn't briefly empty for cached users.
  const recentYap = recentYapLoaded ? fetchedRecentYap : (user.recentYap as any);

  // Live friend count — the denormalized `friendsCount` field on the user doc
  // drifts (the trigger doesn't always fire on accept/remove and the
  // EconomyService.adjustMetric writes can race), so ProfileScreen reads the
  // authoritative `users/{id}/friends` subcollection via UserService.getFriends.
  // Mirror that here so UserDetailModal shows the same number ProfileScreen does.
  const [liveFriendCount, setLiveFriendCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let active = true;
    (async () => {
      try {
        const friends = await UserService.getFriends(String(user.id));
        if (active) setLiveFriendCount(Array.isArray(friends) ? friends.length : 0);
      } catch (err) {
        console.warn('[UserDetailModal] Failed to fetch live friend count:', err);
        if (active) setLiveFriendCount(null);
      }
    })();
    return () => { active = false; };
  }, [isOpen, user?.id]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // If the main image fails, try the first preview image before falling back
    if (portfolioPreviews.length > 0 && e.currentTarget.src !== portfolioPreviews[0]) {
      e.currentTarget.src = portfolioPreviews[0];
    } else if (portfolioImages.length > 0 && e.currentTarget.src !== portfolioImages[0]) {
      e.currentTarget.src = portfolioImages[0];
    } else {
      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&color=fff&size=128`;
    }
  };

  if (typeof document === 'undefined') return null;

  // Blocked either way (I blocked them, or they blocked me): never open their
  // card. The lists that lead here are filtered, but the modal can also be
  // opened from a stale list or a card rendered before the block landed.
  if (isBlockedId((user as any)?.id)) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ zIndex: 10000 }}
            className="fixed inset-0 bg-black/80 z-[10000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ backgroundColor: theme === 'dark' ? '#000000' : '#FAEBD7', zIndex: 10001 }}
            className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl max-h-[85vh] md:max-h-[90vh] rounded-3xl shadow-2xl z-[10001] overflow-hidden modal-shadow isolate flex flex-col"
          >
            {/* Action Buttons Container */}
            <div 
              className="absolute right-4 flex items-center gap-2 pointer-events-auto"
              style={{ top: 'max(16px, env(safe-area-inset-top))', zIndex: 60 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isOwner && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowActionMenu(!showActionMenu); }}
                    className="w-10 h-10 rounded-full bg-black/20 dark:bg-black/50 backdrop-blur-md flex items-center justify-center hover:bg-black/30 transition-colors text-white shadow-md active:bg-black/40"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                  
                  {/* Action Menu Dropdown */}
                  <AnimatePresence>
                    {showActionMenu && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        style={{ zIndex: 9999 }}
                        className="absolute right-0 top-12 w-52 bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl border border-black/5 dark:border-white/5 overflow-hidden py-1 origin-top-right"
                      >
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            setShowActionMenu(false);
                            const reason = window.prompt(`Why are you reporting ${user.username || user.name}? (Harassment, Impersonation, etc.)`);
                            if (!reason || reason.trim().length < 4) {
                              if (reason !== null) toast.error('A valid reason is required to report.');
                              return;
                            }
                            try {
                              const { reportToWatchers } = await import('../services/WatcherService');
                              await reportToWatchers(
                                String(currentUserProfile?.id || ''),
                                String(user.id),
                                'user',
                                reason.trim(),
                                { targetUserId: String(user.id), reportedUsername: user.username || user.name }
                              );
                              toast.success('Report submitted. Our moderators will review this.');
                            } catch {
                              toast.error('Failed to submit report.');
                            }
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 text-sm font-semibold text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors active:bg-black/10"
                        >
                          <AlertTriangle className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                          Report User
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            setShowActionMenu(false);
                            if (window.confirm(`Are you sure you want to block ${user.name}? They will no longer appear in your feed.`)) {
                              await blockUser(String(user.id));
                              onClose();
                            }
                          }}
                          className="w-full px-4 py-3 flex items-center gap-3 text-sm font-semibold text-[#E74C3C] dark:text-[#fc5f5f] hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-black/5 dark:border-white/5 active:bg-red-100 dark:active:bg-red-900/40"
                        >
                          <Ban className="w-4 h-4" />
                          Block User
                        </button>
                        {currentUserProfile?.rank === 'legion' && user.rank !== 'watcher' && user.rank !== 'legion' && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setShowActionMenu(false);
                              try {
                                const db = await getDbSafe();
                                await updateDoc(doc(db, 'users', String(user.id)), { rank: 'watcher' });
                                toast.success(`${user.username} has been promoted to Watcher!`);
                              } catch (err) {
                                console.error('Failed to promote user:', err);
                                toast.error('Failed to promote user');
                              }
                            }}
                            className="w-full px-4 py-3 flex items-center gap-3 text-sm font-semibold text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-t border-black/5 dark:border-white/5 active:bg-purple-100 dark:active:bg-purple-900/40"
                          >
                            <Shield className="w-4 h-4" />
                            Promote to Watcher
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-10 h-10 rounded-full bg-black/20 dark:bg-black/50 backdrop-blur-md flex items-center justify-center hover:bg-black/30 transition-colors text-white shadow-md active:bg-black/40"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Native Scroll Container for better mobile support */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="pb-24" style={{ backgroundColor: theme === 'dark' ? '#000000' : '#FAEBD7' }}>

                {/* 1. Cover Image Header */}
                <div className="h-40 w-full relative">
                  <img
                    src={coverImage}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-b from-black/30 to-transparent opacity-60" />
                </div>

                <div className="px-6 relative">
                  {/* 2. Profile Picture (Overlapping) */}
                  <div 
                    className="relative -mt-16 mb-4 inline-block z-20"
                  >
                    <LegionAvatarWrapper rank={user.rank}>
                      <Avatar 
                        onClick={() => setShowPictureModal(true)}
                        className={`w-32 h-32 shadow-2xl cursor-pointer transition-transform hover:scale-105 active:scale-95 ${['legion'].includes(user.rank as string) ? 'border-none' : 'border-4 border-white dark:border-[#121212]'} bg-gray-200 dark:bg-gray-800 ${!['legion'].includes(user.rank as string) ? getRankRingParams(user.rank).ringClass : ''}`}
                      >
                        <AvatarImage
                          src={(user.portfolioPreviews && user.portfolioPreviews[user.profilePictureIndex ?? 0]) || user.croppedAvatar || user.avatar || user.image}
                          onError={handleImageError}
                          className="object-cover object-center"
                        />
                        <AvatarFallback>{(user.name || '?')[0]}</AvatarFallback>
                      </Avatar>
                    </LegionAvatarWrapper>
                    {/* Country Flag Badge - Sticker Style */}
                    {(user.countryFlag || user.country) && (
                      <div 
                        className="absolute top-0 right-0 w-14 h-auto pointer-events-none z-[1000] flex items-center justify-center"
                        style={{ 
                          transform: 'translate(10%, -20%) rotate(15deg) translateZ(0)',
                          filter: 'drop-shadow(4px 4px 12px rgba(0,0,0,0.6))',
                          willChange: 'transform',
                          zIndex: 1000
                        }}
                      >
                        <FlagImage 
                          countryName={user.countryFlag || user.country} 
                          className="w-full h-auto"
                        />
                      </div>
                    )}
                  </div>

                  {/* 3. User Info Header */}
                  {/* A column, not a row with the socials beside it. Side by side,
                      a long display name had nowhere to go: the socials are
                      shrink-0, and the name span could not shrink below its
                      longest word, so the name overflowed and painted straight
                      over the handles. */}
                  <div className="mb-6 flex flex-col gap-3">
                    <div className="min-w-0 flex flex-col gap-1">
                      {/* flex-wrap so the age and badge drop to the next line
                          rather than being pushed out of the box by a long name.
                          items-center, deliberately: items-baseline is absent from
                          the committed pre-compiled stylesheet and would render as
                          nothing at all. */}
                      <h2 className="flex flex-wrap items-center text-3xl font-bold text-black dark:text-white leading-tight gap-2">
                        {/* min-w-0 lets the span shrink below its intrinsic width
                            — flex items default to min-width:auto and refuse to.
                            break-words then breaks a single long token, which is
                            what a stylised name like ★NIRVANA★ is. */}
                        <span className="min-w-0 break-words">{user.name || 'Unknown User'}</span>
                        {user.rank && ['watcher', 'legion', 'verified', 'union'].includes(user.rank.toLowerCase()) && (
                          <RankBadge rank={user.rank} size={34} />
                        )}
                        <span className="opacity-40 text-2xl font-light">·</span>
                        <span>{calculateAge(user.dateOfBirth) || user.age}</span>
                        {/* Activity sits after the age, much smaller. Inline styles:
                            the precompiled stylesheet may lack the size utilities. */}
                        {activity.label && (
                          <>
                            <span className="opacity-40 text-2xl font-light">·</span>
                            <span
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 14, lineHeight: 1.2, whiteSpace: 'nowrap',
                                color: activity.isActiveNow ? '#22C55E' : '#8e8e8e',
                                fontWeight: activity.isActiveNow ? 600 : 400,
                              }}
                            >
                              {/* Glowing dot within the 48h "recently active" window
                                  (now, minutes ago, today), same as the Discover badge.
                                  Dot and glow are one radial gradient in a fixed square box:
                                  a box-shadow glow on a sub-10px em-sized dot rendered as two
                                  conjoined dots. Negative margin keeps the glow from pushing
                                  the text away. */}
                              {activity.isRecentlyActive && (
                                <GlowDot
                                  style={{
                                    width: 18, height: 18, minWidth: 18, minHeight: 18, maxWidth: 18, maxHeight: 18,
                                    margin: '0 -4px',
                                    boxShadow: 'none',
                                    // ~8px solid core, then a soft halo fading out to the edge.
                                    background:
                                      'radial-gradient(circle closest-side, #4ADE80 0, #4ADE80 44%, rgba(74,222,128,0.6) 54%, rgba(74,222,128,0.25) 75%, rgba(74,222,128,0) 100%)',
                                  }}
                                />
                              )}
                              {activity.label}
                            </span>
                          </>
                        )}
                      </h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-lg text-[#666666] dark:text-[#E5E5E5] font-medium">@{user.username?.replace(/^@/, '') || 'unknown'}</p>
                        <span className="opacity-40 text-lg font-light">·</span>
                        {/* Friend count — same size as the username, sits inline next to it.
                            Prefer the live subcollection count (matches what ProfileScreen
                            shows); fall back to the denormalized field until the fetch
                            resolves so the row isn't briefly empty.
                            Inline style is intentional: a stray cascading colour from
                            ancestor wrappers was beating Tailwind's arbitrary class
                            and leaving the text white. Inline style wins the cascade. */}
                        {(() => {
                          const u = user as any;
                          const fallback =
                            typeof u.friendsCount === 'number'
                              ? u.friendsCount
                              : Array.isArray(u.friends) ? u.friends.length : 0;
                          const count = liveFriendCount ?? fallback;
                          return (
                            <p
                              className="text-lg font-medium"
                              style={{ color: '#4478ea' }}
                            >
                              <span className="font-bold" style={{ color: '#4478ea' }}>
                                {count.toLocaleString()}
                              </span>{' '}
                              <span style={{ color: '#4478ea' }}>
                                {count === 1 ? 'friend' : 'friends'}
                              </span>
                            </p>
                          );
                        })()}
                        {!isOwner && isAlreadyFriend && <FriendBadge />}
                        {!isOwner && !isAlreadyFriend && isSimilar && <SimilarBadge />}
                      </div>

                      <div className="flex items-center gap-2 mt-2 text-[#373737] dark:text-[#e0e0e0]">
                        <MapPin className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                        <span className="text-sm font-medium">
                          {user.showLocation === false ? 'Earth' : (user.home_residence?.trim() || user.location?.trim() || user.country?.trim() || 'Earth')}
                        </span>
                      </div>

                      {/* Connect button — costs 10 gold (same flow as ProfilePreview + Newest Yappers). */}
                      {!isOwner && (
                        <motion.button
                          whileTap={(connectState === 'idle' || connectState === 'accept' || connectState === 'sent') ? { scale: 0.96 } : {}}
                          disabled={connectState === 'loading' || connectState === 'friend'}
                          onClick={handleConnect}
                          className="mt-3 self-start inline-flex items-center gap-2 h-9 px-4 rounded-full text-white text-sm font-bold shadow-md"
                          style={{
                            background:
                              connectState === 'friend' ? 'linear-gradient(90deg, #1e6b2c, #154f20)' :
                              connectState === 'sent'   ? 'rgba(0,0,0,0.10)' :
                              'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
                            color: connectState === 'sent' ? 'rgba(0,0,0,0.55)' : '#ffffff',
                            cursor: connectState === 'friend' ? 'default' : 'pointer',
                          }}
                        >
                          {connectState === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
                          {connectState === 'idle' && (
                            <>
                              <UserPlus className="w-4 h-4" />
                              <img src={goldIcon as any} alt="" className="h-4 w-auto object-contain" />
                              <span>{connectCost} Gold</span>
                            </>
                          )}
                          {connectState === 'accept' && (
                            <>
                              <UserPlus className="w-4 h-4" />
                              <span>Accept</span>
                            </>
                          )}
                          {connectState === 'sent' && (
                            <>
                              <span>Request Sent</span>
                              <X className="w-3.5 h-3.5" />
                            </>
                          )}
                          {connectState === 'friend' && (
                            <>
                              <UserCheck className="w-4 h-4" />
                              <span>Friends ✓</span>
                            </>
                          )}
                        </motion.button>
                      )}
                    </div>

                    {/* Own line below the name block — only platforms with a
                        handle render, so this collapses to nothing for a user
                        with none and the header keeps its original height.

                        direction and flex-wrap are set HERE rather than in
                        SocialLinks: ProfileScreen shares that component and its
                        layout should not move. flex-wrap is a class because the
                        component sets display/flexDirection inline but not
                        flexWrap, so the class applies cleanly — without it two
                        handles at up to 160px each can overrun the modal. */}
                    <SocialLinks socials={(user as any).socials} size={32} direction="row" gap={10} className="flex-wrap" />
                  </div>

                  {/* 4. Bio Section */}
                  {isPrivate ? (
                    <div className="mb-8 p-8 rounded-3xl bg-gray-100/50 dark:bg-white/5 border border-dashed border-gray-300 dark:border-white/10 flex flex-col items-center justify-center text-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-[#F39C12]/10 dark:bg-[#4478ea]/10 flex items-center justify-center">
                        <Lock className="w-8 h-8 text-[#D35400] dark:text-[#4478ea]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-black dark:text-white mb-2">Private Profile</h3>
                        <p className="text-sm text-[#666666] dark:text-[#999999] max-w-[250px]">
                          Connect with {user.name || 'this user'} to see their bio, yaps, and portfolio.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-black dark:text-white mb-2">About</h3>
                      <div className="relative">
                        <FormattedText 
                          text={showFullBio ? (user.bio || '') : (user.bio || '').slice(0, 160)} 
                          className="text-[#333333] dark:text-[#E5E5E5] leading-relaxed text-base block whitespace-pre-wrap transition-all" 
                        />
                        {(user.bio || '').length > 160 && (
                          <button
                            onClick={() => setShowFullBio(!showFullBio)}
                            className="mt-1 text-[#F39C12] dark:text-[#4478ea] font-bold text-sm hover:underline active:opacity-70"
                          >
                            {showFullBio ? 'See Less' : '... See More'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}


                  {/* 5. Recent Yap (Embedded) */}
                  {!isPrivate && (
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold text-black dark:text-white">Recent Yap</h3>
                        {recentYap && (
                          <span className="text-xs text-[#999999] bg-gray-100 dark:bg-white/5 px-2 py-1 rounded-full">
                            {formatTimeAgo(recentYap.timestamp)}
                          </span>
                        )}
                      </div>

                      {recentYap ? (
                        <motion.div
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('navigateToYapDetail', {
                              detail: {
                                // Use the real yap doc id from the live fetch so
                                // tapping the card opens the actual yap. The
                                // legacy fallback (`yap-<uid>-recent`) is kept
                                // for the cached snapshot case where we don't
                                // have a real id.
                                id: (recentYap as any).id || `yap-${user.id}-recent`,
                                author: user.name || 'Unknown User',
                                username: user.username || 'unknown',
                                avatar: user.image,
                                content: recentYap.content,
                                time: recentYap.timestamp,
                                likes: recentYap.likes,
                                comments: recentYap.comments,
                                country: user.country,
                                visibility: 'public',
                              }
                            }));
                          }}
                          className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all cursor-pointer relative overflow-hidden group"
                        >
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-linear-to-b from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1]" />

                          <p className="text-base text-[#333333] dark:text-[#e0e0e0] line-clamp-3 mb-3 font-medium">
                            {recentYap.content}
                          </p>

                          {/* Yap media — custom grid that opens the shared
                              ProfilePictureModal on tap. We deliberately do NOT
                              use YapImageGrid here: its ImageWithPreview
                              lightbox is rendered as a descendant of THIS
                              modal, but UserDetailModal's wrapper has
                              `isolate` + an animated `transform`, which makes
                              the lightbox's fixed-position positioning relative
                              to the modal instead of the viewport — so it
                              renders clipped/invisible. ProfilePictureModal is
                              already a sibling of the modal card and works. */}
                          {Array.isArray((recentYap as any).images) && (recentYap as any).images.length > 0 && (() => {
                            const imgs = (recentYap as any).images.filter(Boolean).slice(0, 4) as string[];
                            return (
                              <div
                                className="mb-3 grid gap-1.5 rounded-2xl overflow-hidden"
                                style={{
                                  gridTemplateColumns: imgs.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                                  aspectRatio: imgs.length === 1 ? '16 / 9' : '1 / 1',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {imgs.map((src, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setViewingYapImageIndex(i);
                                    }}
                                    className="block w-full h-full p-0 m-0 border-none bg-transparent cursor-zoom-in"
                                  >
                                    <img
                                      src={src}
                                      alt={`Yap media ${i + 1}`}
                                      className="w-full h-full object-cover block"
                                    />
                                  </button>
                                ))}
                              </div>
                            );
                          })()}

                          <div className="flex items-center gap-4 text-xs font-medium text-[#999999] dark:text-[#808080]">
                            <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {recentYap.likes}</span>
                            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {recentYap.comments}</span>
                          </div>
                        </motion.div>
                      ) : (
                        <div className="p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-dashed border-gray-200 dark:border-white/10 text-center">
                          <p className="text-gray-400 dark:text-gray-500 text-sm">No recent yaps</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 6. Identity (Unique Tags) */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-black dark:text-white mb-3">Identity</h3>
                    <div className="flex flex-wrap gap-3">
                      {user.identities?.map((identity, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <IdentityTag tag={identity} variant="profile" />
                          </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* 7. Tribes (Interests) - Only if allowed */}
                  {(user.showTribes !== false) && user.interests && user.interests.length > 0 && (() => {
                    const isTribeListHidden = user.showTribesList === false;
                    const tVisibility = user.tribeListVisibility || 'everyone';
                    
                    // derivedIsFriend calculation (Need to check context or use a simple check)
                    // UserDetailModal doesn't have useUserData directly for isFriend check on 'user' prop easily without isFriend helper
                    // But we can check if current user is the owner (isOwner already defined)
                    // For friend check, we'd need useUserData().isFriend(user.id)
                    
                    // I will assume for simplicity here that if isOwner is false, we check the flags.
                    // To be thorough, I should use the isFriend check if possible.
                    // UserDetailModal already has currentUserProfile from useUserData.
                    
                    const viewerIsFriend = isOwner || isFriend(String(user.id));
                    const canSeeFullTribes = isOwner || (
                      !isTribeListHidden && (
                        tVisibility === 'everyone' ||
                        (tVisibility === 'friends' && viewerIsFriend)
                      )
                    );

                    return (
                      <div className="mb-8">
                        <h3 className="text-lg font-bold text-black dark:text-white mb-3">
                          Tribes ({user.interests.length})
                        </h3>
                        {!canSeeFullTribes ? (
                          <div className="p-4 rounded-xl bg-gray-100/50 dark:bg-white/5 border border-dashed border-gray-200 dark:border-white/10 text-center">
                            <p className="text-xs text-gray-400 dark:text-gray-500">Tribe list is hidden</p>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {user.interests.map((interest, index) => (
                              <motion.span
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="px-4 py-2 bg-gray-100 dark:bg-white/5 border border-transparent dark:border-white/10 rounded-xl text-sm font-medium text-[#333333] dark:text-[#e0e0e0]"
                              >
                                {interest}
                              </motion.span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* 8. Portfolio Grid */}
                  {!isPrivate && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-black dark:text-white mb-3">Portfolio</h3>
                      <div className="grid gap-3" style={{ 
                         gridTemplateColumns: (portfolioPreviews.length > 0 ? portfolioPreviews : portfolioImages).slice(0, 4).length === 1 ? '1fr' : 'repeat(2, 1fr)'
                      }}>
                        {(portfolioPreviews.length > 0 ? portfolioPreviews : portfolioImages).slice(0, 4).map((img, index, arr) => {
                          const isThree = arr.length === 3;
                          const isFirst = index === 0;
                          return (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1 }}
                              onClick={() => img && setViewingPortfolioIndex(index)}
                              className={`relative aspect-3/4 w-full rounded-2xl overflow-hidden group bg-gray-100 dark:bg-[#27272a] ${
                                img ? 'cursor-pointer' : 'cursor-default'
                              } ${
                                isThree && isFirst ? 'col-span-2 aspect-3/2' : ''
                              }`}
                            >
                              {img ? (
                                <div className="w-full h-full relative">
                                  <img
                                    src={img}
                                    alt={`Portfolio ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 object-center"
                                    loading="lazy"
                                  />
                                  {isMaybeVideo(img) && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                      <Play className="w-8 h-8 text-white fill-white opacity-70" />
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center opacity-20">
                                  <X className="w-12 h-12 text-black dark:text-white" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </motion.div>
          
          <ProfilePictureModal
            isOpen={showPictureModal}
            onClose={() => setShowPictureModal(false)}
            imageUrl={(user.portfolioPreviews && user.portfolioPreviews[user.profilePictureIndex ?? 0]) || user.croppedAvatar || user.avatar || user.image}
            images={portfolioPreviews.length > 0 ? portfolioPreviews : portfolioImages}
            videoUrls={user.portfolioVideos}
            photoMetadata={user.photoMetadata}
            initialIndex={user.profilePictureIndex ?? 0}
            userName={user.name || 'Unknown User'}
          />
          
          <ProfilePictureModal
            isOpen={viewingPortfolioIndex !== null}
            onClose={() => setViewingPortfolioIndex(null)}
            images={portfolioPreviews.length > 0 ? portfolioPreviews : portfolioImages}
            videoUrls={user.portfolioVideos}
            photoMetadata={user.photoMetadata}
            initialIndex={viewingPortfolioIndex !== null ? viewingPortfolioIndex : 0}
            userName={user.name || 'Unknown User'}
          />

          {/* Yap-media lightbox — separate ProfilePictureModal so the recent yap
              card's image taps open a fullscreen viewer that escapes the user
              detail modal's clipping/isolate context. */}
          <ProfilePictureModal
            isOpen={viewingYapImageIndex !== null}
            onClose={() => setViewingYapImageIndex(null)}
            images={Array.isArray((recentYap as any)?.images) ? (recentYap as any).images : []}
            initialIndex={viewingYapImageIndex !== null ? viewingYapImageIndex : 0}
            userName={user.name || 'Unknown User'}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
